import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { useRequestLazy } from '~/hooks/use-request-lazy';
import { setJWT } from '~/localstrage/jwt';
import { getSlug } from '~/localstrage/slug';
import {
  loginState,
  loginWithNewPasswordState,
  loginWithOnetimeCodeState,
} from '~/state/login/atoms';
import { announceOrganizationBusinessHourModalState } from '~/state/partials/announce_organization_business_hour_modal/atoms';

import { Fields } from './Account/types';
import { parseNextPage } from './parser';

type Parameter = {
  organizationSlug: string;
  username: string;
  password: string;
  onetimeCode: string | null;
};

const TokenRequestProps = {
  url: '/practitioners',
  method: 'POST',
} as const;

export const useSigninPractitioner = () => {
  const router = useRouter();
  const afterSignup = router.asPath.replace(router.pathname, '') === '#registered';
  const nextPage = router.query['next'] ? parseNextPage(router.query['next'] as string) : null;
  const resetLoginState = useResetAtom(loginState);
  const setLoginWithNewPasswordState = useSetAtom(loginWithNewPasswordState);
  const setLoginWithOnetimeCodeState = useSetAtom(loginWithOnetimeCodeState);
  const setAnnounceModal = useSetAtom(announceOrganizationBusinessHourModalState);

  const [isLoading, setIsLoading] = useState(false);

  const [signin, { error }] = useRequestLazy<Parameter, { jwt: string }>(TokenRequestProps);

  const handleSubmit = useCallback(
    async (values: Fields, onetimeCode?: string) => {
      const slug = getSlug();
      if (!slug) {
        resetLoginState();
        return;
      }

      setIsLoading(true);
      const { data, error: _error } = await signin({
        data: {
          organizationSlug: slug,
          ...values,
          onetimeCode: onetimeCode || null,
        },
      });

      if (_error?.responseData) {
        if (_error.responseData['required'] === 'set_password') {
          setLoginWithNewPasswordState({
            isRequired: true,
            username: values.username,
            practitionerCode: values.password,
          });
        }
        if (_error.responseData['required'] === 'onetime_code') {
          setLoginWithOnetimeCodeState({
            isRequired: true,
            username: values.username,
            password: values.password,
          });
        }
        setIsLoading(false);
        return;
      }
      if (_error || !data) {
        setIsLoading(false);
        return;
      }

      setJWT(data.jwt);
      setAnnounceModal((prev) => ({ ...prev, afterLogin: true }));
      router.push(nextPage || '/');
    },
    [
      nextPage,
      resetLoginState,
      router,
      setAnnounceModal,
      setLoginWithNewPasswordState,
      setLoginWithOnetimeCodeState,
      signin,
    ],
  );

  useEffect(() => {
    return () => {
      setIsLoading(false);
    };
  }, []);

  return {
    error,
    loading: isLoading,
    afterSignup,
    handleSubmit,
  };
};
